import { useUsernameAvailability } from "@/shared/api/check-username.api";
import { Input } from "../ui/input";
import { useTranslation } from "react-i18next";
import { useDebounceValue } from "usehooks-ts";
import CheckIcon from "@/assets/icons/check.svg?react";
import CloseIcon from "@/assets/icons/close.svg?react";
import { cn } from "@/lib/utils";
import { useEffect } from "react";
import { usernameSchema } from "@/shared/schema/username";

interface UsernameFormProps extends React.ComponentProps<"input"> {
  onAvailabilityChange: (isAvailable: boolean) => void;
  initialUsername: string;
  onValidationChange?: (isValid: boolean) => void;
  onDebouncingChange?: (isDebouncing: boolean) => void; // New prop
  onCheckLoading?: (isLoading: boolean) => void; // New prop
}

const UsernameForm: React.FC<UsernameFormProps> = (props) => {
  const {
    value,
    onAvailabilityChange,
    initialUsername,
    onValidationChange,
    onDebouncingChange,
    onCheckLoading,
    ...restProps
  } = props;
  const { t } = useTranslation();
  const [debouncedValue] = useDebounceValue(value, 500);
  const isDebouncing = value !== debouncedValue;
  const parsedUsername = usernameSchema.safeParse(debouncedValue);
  const isChanged = debouncedValue !== initialUsername;
  const { data, isLoading } = useUsernameAvailability(
    isChanged ? parsedUsername.data || "" : ""
  );

  useEffect(() => {
    onDebouncingChange?.(isDebouncing);
  }, [isDebouncing, onDebouncingChange]);

  useEffect(() => {
    onCheckLoading?.(isLoading);
  }, [isLoading, onCheckLoading]);

  useEffect(() => {
    if (onValidationChange) {
      onValidationChange(parsedUsername.success);
    }
  }, [parsedUsername, onValidationChange]);

  useEffect(() => {
    if (onAvailabilityChange && parsedUsername.data) {
      onAvailabilityChange(!!data?.username_available);
    }
    if (!isChanged || debouncedValue === "") {
      onAvailabilityChange(true);
    }
  }, [
    data?.username_available,
    parsedUsername.data,
    onAvailabilityChange,
    isChanged,
    debouncedValue,
  ]);

  return (
    <div className="relative">
      {!!parsedUsername.data && isChanged && (
        <div className="absolute right-0 -top-[calc(24px+8px)]">
          {isLoading ? (
            <img
              width="20"
              height="20"
              src="loader.svg"
              alt="loading"
              className="animate-spin-slow"
            />
          ) : (
            <span
              className={cn("text-sm inline-flex gap-1 items-center", {
                "text-green": data?.username_available,
                "text-red": !data?.username_available,
              })}
            >
              {data?.username_available ? (
                <>
                  {t("username_available")}
                  <CheckIcon className="size-4" />
                </>
              ) : (
                <>
                  {t("username_not_available")}
                  <CloseIcon className="size-4" />
                </>
              )}
            </span>
          )}
        </div>
      )}
      <Input
        placeholder={t("enter_username")}
        autoComplete="off"
        autoCorrect="off"
        spellCheck={false}
        className="lowercase"
        value={value}
        {...restProps}
      />
    </div>
  );
};

export default UsernameForm;
